import React, { useEffect, useMemo, useState } from 'react'
import { Text, Heading, Sidebar, SortableTableHeader, LoadingSpinner, Button, Input, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { ExclamationTriangleIcon, EyeIcon, XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { supabase } from '../lib/supabase'

interface CreditUsage {
  id: number
  created_at: string
  json: string | null
  credits: number | null
  platform: string | null
  process: string | null
  tokens: number | null
}

const AgentesIAConsumo: React.FC = () => {
  const [data, setData] = useState<CreditUsage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [sortBy, setSortBy] = useState<'id' | 'created_at' | 'platform' | 'process' | 'credits' | 'tokens'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false)
  const [selectedJson, setSelectedJson] = useState<string>('')

  // Filtros
  const [filtroPlataforma, setFiltroPlataforma] = useState('')
  const [filtroProcesso, setFiltroProcesso] = useState('')
  const [filtroDataInicial, setFiltroDataInicial] = useState('')
  const [filtroDataFinal, setFiltroDataFinal] = useState('')
  const [filtroMinCredits, setFiltroMinCredits] = useState('')
  const [filtroMaxCredits, setFiltroMaxCredits] = useState('')
  const [filtroMinTokens, setFiltroMinTokens] = useState('')
  const [filtroMaxTokens, setFiltroMaxTokens] = useState('')

  const [plataformasDisponiveis, setPlataformasDisponiveis] = useState<string[]>([])
  const [processosDisponiveis, setProcessosDisponiveis] = useState<string[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Buscar todos os registros usando paginação
        const allData: CreditUsage[] = []
        const batchSize = 1000
        let from = 0
        let hasMore = true

        while (hasMore) {
          const { data, error } = await supabase
            .from('credit_usage')
            .select('id, created_at, json, credits, platform, process, tokens')
            .order('created_at', { ascending: false })
            .range(from, from + batchSize - 1)

          if (error) throw error

          if (data && data.length > 0) {
            allData.push(...(data as CreditUsage[]))
            
            // Se retornou menos que o batchSize, não há mais dados
            if (data.length < batchSize) {
              hasMore = false
            } else {
              from += batchSize
            }
          } else {
            hasMore = false
          }
        }

        setData(allData)
        const plats = allData.map(r => r.platform).filter(Boolean) as string[]
        setPlataformasDisponiveis(Array.from(new Set(plats)).sort())
        const procs = allData.map(r => r.process).filter(Boolean) as string[]
        setProcessosDisponiveis(Array.from(new Set(procs)).sort())
      } catch (err: any) {
        console.error('Erro ao carregar credit_usage:', err)
        setError('Erro ao carregar dados de consumo de créditos')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }

  const openJsonModal = (rawJson: string | null) => {
    setSelectedJson(formatJson(rawJson))
    setIsJsonModalOpen(true)
  }

  const closeJsonModal = () => {
    setIsJsonModalOpen(false)
    setSelectedJson('')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const formatNumber = (value: number | null) => {
    if (value === null || value === undefined) return '-'
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value)
  }

  const formatJson = (raw: string | null) => {
    if (!raw) return 'Sem dados'
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      return JSON.stringify(parsed, null, 2)
    } catch {
      return raw
    }
  }

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchPlataforma = !filtroPlataforma || item.platform === filtroPlataforma
      const matchProcesso = !filtroProcesso || item.process === filtroProcesso

      const createdAtTime = new Date(item.created_at).getTime()
      const startOk = !filtroDataInicial || createdAtTime >= new Date(filtroDataInicial).getTime()
      const endOk = !filtroDataFinal || createdAtTime <= new Date(filtroDataFinal + 'T23:59:59').getTime()

      const credits = item.credits ?? null
      const minCreditsOk = !filtroMinCredits || (credits !== null && credits >= Number(filtroMinCredits))
      const maxCreditsOk = !filtroMaxCredits || (credits !== null && credits <= Number(filtroMaxCredits))

      const tokens = item.tokens ?? null
      const minTokensOk = !filtroMinTokens || (tokens !== null && tokens >= Number(filtroMinTokens))
      const maxTokensOk = !filtroMaxTokens || (tokens !== null && tokens <= Number(filtroMaxTokens))

      return matchPlataforma && matchProcesso && startOk && endOk && minCreditsOk && maxCreditsOk && minTokensOk && maxTokensOk
    })
  }, [data, filtroPlataforma, filtroProcesso, filtroDataInicial, filtroDataFinal, filtroMinCredits, filtroMaxCredits, filtroMinTokens, filtroMaxTokens])

  const sortedData = useMemo(() => {
    const copy = [...filteredData]
    return copy.sort((a, b) => {
      let aValue: any = (a as any)[sortBy]
      let bValue: any = (b as any)[sortBy]

      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else if (sortBy === 'id') {
        aValue = Number(aValue)
        bValue = Number(bValue)
      } else if (sortBy === 'credits' || sortBy === 'tokens') {
        aValue = aValue ?? -Infinity
        bValue = bValue ?? -Infinity
      } else {
        aValue = (aValue ?? '').toString().toLowerCase()
        bValue = (bValue ?? '').toString().toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortBy, sortOrder])

  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder, filtroPlataforma, filtroProcesso, filtroDataInicial, filtroDataFinal, filtroMinCredits, filtroMaxCredits, filtroMinTokens, filtroMaxTokens])

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const pagedData = sortedData.slice(startIndex, endIndex)

  const totalCredits = useMemo(() => {
    return filteredData.reduce((sum, r) => sum + (r.credits || 0), 0)
  }, [filteredData])

  const totalTokens = useMemo(() => {
    return filteredData.reduce((sum, r) => sum + (r.tokens || 0), 0)
  }, [filteredData])

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" color="muted">Carregando dados...</Text>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              {error}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard 
          moduleName="configuracoes_consumo" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <SparklesIcon className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      Agentes IA - Consumo
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">
                      Histórico de uso de créditos por plataforma e processo
                    </Text>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {sortedData.length}
                      </Text>
                      <Text size="sm" className="text-white">Total de Registros</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {formatNumber(totalCredits)}
                      </Text>
                      <Text size="sm" className="text-white">Total de Créditos</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {formatNumber(totalTokens)}
                      </Text>
                      <Text size="sm" className="text-white">Total de Tokens</Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="mb-4">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Filtros
                    </Heading>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Plataforma</Text>
                      <Select value={filtroPlataforma} onChange={(e) => setFiltroPlataforma(e.target.value)} className="w-full">
                        <option value="">Todas as plataformas</option>
                        {plataformasDisponiveis.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Processo</Text>
                      <Select value={filtroProcesso} onChange={(e) => setFiltroProcesso(e.target.value)} className="w-full">
                        <option value="">Todos os processos</option>
                        {processosDisponiveis.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                      <Input type="date" value={filtroDataInicial} onChange={(e) => setFiltroDataInicial(e.target.value)} className="w-full" />
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                      <Input type="date" value={filtroDataFinal} onChange={(e) => setFiltroDataFinal(e.target.value)} className="w-full" />
                    </div>

                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Créditos (mín.)</Text>
                      <Input type="number" placeholder="0" value={filtroMinCredits} onChange={(e) => setFiltroMinCredits(e.target.value)} className="w-full" />
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Créditos (máx.)</Text>
                      <Input type="number" placeholder="" value={filtroMaxCredits} onChange={(e) => setFiltroMaxCredits(e.target.value)} className="w-full" />
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Tokens (mín.)</Text>
                      <Input type="number" placeholder="0" value={filtroMinTokens} onChange={(e) => setFiltroMinTokens(e.target.value)} className="w-full" />
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Tokens (máx.)</Text>
                      <Input type="number" placeholder="" value={filtroMaxTokens} onChange={(e) => setFiltroMaxTokens(e.target.value)} className="w-full" />
                    </div>
                    <div className="md:col-span-2 flex justify-start">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFiltroPlataforma('')
                          setFiltroProcesso('')
                          setFiltroDataInicial('')
                          setFiltroDataFinal('')
                          setFiltroMinCredits('')
                          setFiltroMaxCredits('')
                          setFiltroMinTokens('')
                          setFiltroMaxTokens('')
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 p-6 pt-0">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
                  <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                      Histórico de Consumo
                    </Heading>
                    <div className="flex-1 overflow-auto">
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                          <tr>
                            <SortableTableHeader sortKey="id" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-20 text-left">
                              ID
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-48 text-left">
                              Data
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="platform" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-40 text-left">
                              Plataforma
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="process" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-40 text-left">
                              Processo
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="credits" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-32 text-right">
                              Créditos
                            </SortableTableHeader>
                            <SortableTableHeader sortKey="tokens" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-32 text-right">
                              Tokens
                            </SortableTableHeader>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-32">
                              JSON
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {pagedData.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-4 text-left">
                                <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                                  {item.id}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-left">
                                <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                  {formatDate(item.created_at)}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-left">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
                                  {item.platform || '-'}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-left">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
                                  {item.process || '-'}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {formatNumber(item.credits)}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                                  {formatNumber(item.tokens)}
                                </Text>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <Button onClick={() => openJsonModal(item.json)} size="sm" className="inline-flex items-center gap-2">
                                  <EyeIcon className="h-4 w-4" />
                                  Ver JSON
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                              <Text size="sm">
                                Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
                              </Text>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                Anterior
                              </button>
                              <div className="flex space-x-1">
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                  let pageNumber: number
                                  if (totalPages <= 5) {
                                    pageNumber = i + 1
                                  } else if (currentPage <= 3) {
                                    pageNumber = i + 1
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNumber = totalPages - 4 + i
                                  } else {
                                    pageNumber = currentPage - 2 + i
                                  }
                                  return (
                                    <button
                                      key={pageNumber}
                                      onClick={() => setCurrentPage(pageNumber)}
                                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                                        currentPage === pageNumber
                                          ? 'bg-blue-600 text-white'
                                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      {pageNumber}
                                    </button>
                                  )
                                })}
                              </div>
                              <button
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                Próximo
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {isJsonModalOpen && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeJsonModal}></div>
                  <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 max-h-[90vh] flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                        Detalhes do JSON
                      </Heading>
                      <button onClick={closeJsonModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto">
                      <pre className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border">
{selectedJson}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </PermissionGuard>
        </main>
      </div>
    </div>
  )
}

export default AgentesIAConsumo


